/**
 * Chapter 05 blocks (SPEC §3.05): change since the prior scan and the monthly
 * local support index. Months resting on ≤3 statements are hollow and never
 * joined; a line joins only consecutive months that each rest on ≥4. Months
 * with no dated local statement stay open gaps (never 50). Statement counts sit
 * in their own column chart on the same months (one axis per chart).
 */
import React from 'react';
import type { ReportTheme } from '../../../../../types/reportTheme';
import { roleColor } from '../../../../../lib/reportRoleColors';
import { EditorialKpiRail } from '../../../report/editorial';
import {
  PSR_TREND_DOTS_ONLY_MAX_MONTHS,
  psrChangeModel,
  psrFormatDay,
  psrLowVolumeNote,
  psrTrendJoinedRuns,
  psrTrendMonths,
  psrWindowIndexLabel,
  type PsrReportView,
  type PsrTrendMonth,
} from '../../../../../lib/sentiment/psrReportView';
import { PSR_LOW_VOLUME_MONTH_MAX } from '../../../../../lib/sentiment/publicSentimentEvidenceTables';
import { PSR_APPARATUS, PSR_CHART_WIDTH, PsrBlock, PsrLegend, PsrNote, PsrPlainNote, PsrTable, psrPalette } from './psrUi';

/* ── Change since the prior scan ───────────────────────────────────────── */

export function SentimentChangeStrip({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  const change = psrChangeModel(view);
  if (!change) return null;
  const { items, note, variant } = change;
  const prior = view.v2.priorScan;
  return (
    <PsrBlock
      theme={theme}
      testId="psr-change-strip"
      title={prior ? 'Change since the last scan' : 'Change since the prior scan'}
      subtitle={variant === 'comparable' ? 'Compared with the most recent prior scan of this site' : undefined}
      partial={view.partial}
      caveat={view.partial ? 'This scan was partial; the prior scan’s coverage is not recorded.' : undefined}
    >
      <div data-change-variant={variant}>
        <EditorialKpiRail theme={theme} items={items.map(i => ({ key: i.key, value: i.value, ...(i.unit ? { unit: i.unit } : {}), ...(i.note ? { note: i.note } : {}) }))} columns={items.length} />
      </div>
      <PsrNote theme={theme}>{note}</PsrNote>
    </PsrBlock>
  );
}

/* ── Monthly local support index ───────────────────────────────────────── */

export function SentimentTrendTable({ theme, months, caption }: { theme: ReportTheme; months: PsrTrendMonth[]; caption?: React.ReactNode }): React.ReactElement | null {
  if (!months.length) return null;
  return (
    <PsrTable
      theme={theme}
      testId="psr-trend-table"
      caption={caption}
      columns={[{ header: 'Month', width: '40%' }, { header: 'Statements', align: 'right', numeric: true }, { header: 'Index', align: 'right', numeric: true }]}
      groups={[{ key: 'months', rows: months.map(m => ({ key: m.month, cells: [`${m.label} ${m.month.slice(0, 4)}`, m.count, m.index !== undefined ? m.index : '—'] })) }]}
    />
  );
}

export function SentimentTrendChart({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  if (view.index === undefined || !view.assessment) return null;
  const months = psrTrendMonths(view.assessment.monthly, view.scan?.fetchedAt, view.windowDays);
  if (!months.some(m => m.index !== undefined)) return null;
  const index = Math.round(view.index);
  const p = psrPalette(theme);
  const ink = roleColor(theme, 'ink');
  const muted = roleColor(theme, 'muted');
  const faint = roleColor(theme, 'faint');
  const rule = roleColor(theme, 'rule');
  const markInk = p.amount[3];
  const X0 = 40;
  const X1 = PSR_CHART_WIDTH - 8;
  const col = (X1 - X0) / months.length;
  const x = (i: number) => X0 + col * (i + 0.5);
  const TOP = 8;
  const PLOT_H = 168;
  const y = (v: number) => TOP + ((100 - v) / 100) * PLOT_H;
  const BASE = 270;
  const maxCount = Math.max(1, ...months.map(m => m.count));
  const barH = (n: number) => (n / maxCount) * 44;
  const runs = psrTrendJoinedRuns(months);
  const dotsOnly = months.filter(m => m.index !== undefined).length <= PSR_TREND_DOTS_ONLY_MAX_MONTHS;
  const joined = new Set(runs.flatMap(([a, b]) => Array.from({ length: b - a + 1 }, (_, k) => a + k)));
  const indexLabel = psrWindowIndexLabel(view.windowDays);
  const low = psrLowVolumeNote(months, index, view.windowDays);
  const tickText = { fontFamily: PSR_APPARATUS, fontSize: 12, fontVariantNumeric: 'tabular-nums' } as const;
  const small = { fontFamily: PSR_APPARATUS, fontSize: 11, fontWeight: 500 } as const;
  const scanDay = psrFormatDay(view.scan?.fetchedAt);
  const hollow = (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" style={{ display: 'block', flex: 'none' }}>
      <circle cx="7" cy="7" r="4" fill={p.surface} stroke={markInk} strokeWidth="2" />
    </svg>
  );
  const solid = (
    <svg width="30" height="14" viewBox="0 0 30 14" aria-hidden="true" style={{ display: 'block', flex: 'none' }}>
      {!dotsOnly && <line x1="2" y1="7" x2="28" y2="7" stroke={markInk} strokeWidth="2" strokeLinecap="round" />}
      <circle cx="15" cy="7" r="6.5" fill={p.surface} />
      <circle cx="15" cy="7" r="4.5" fill={markInk} />
    </svg>
  );
  const lineKey = (w: number, color: string, width: number) => (
    <svg width={w} height="14" viewBox={`0 0 ${w} 14`} aria-hidden="true" style={{ display: 'block', flex: 'none' }}>
      <line x1="1" y1="7.5" x2={w - 1} y2="7.5" stroke={color} strokeWidth={width} strokeLinecap="round" />
    </svg>
  );
  return (
    <PsrBlock
      theme={theme}
      testId="psr-trend-chart"
      title="Local support index by month"
      subtitle={`0–100 · ${months.length} months${scanDay ? ` to ${scanDay}` : ''} · statement counts below on the same months`}
      partial={view.partial}
      caveat={low ? undefined : 'A month without a point may be a coverage gap, not a quiet month.'}
      table={<SentimentTrendTable theme={theme} months={months} />}
    >
      <PsrLegend
        theme={theme}
        items={[
          { key: 'low', swatch: hollow, label: `${PSR_LOW_VOLUME_MONTH_MAX} or fewer statements · directional only, not joined` },
          { key: 'solid', swatch: solid, label: dotsOnly ? `${PSR_LOW_VOLUME_MONTH_MAX + 1} or more · not joined: too few months` : `${PSR_LOW_VOLUME_MONTH_MAX + 1} or more · joined by a line` },
          { key: 'balanced', swatch: lineKey(22, muted, 1), label: '50 · balanced' },
          { key: 'window', swatch: lineKey(18, markInk, 2), label: `${index} · ${indexLabel}` },
        ]}
      />
      <svg data-testid="psr-trend-svg" viewBox={`0 0 ${PSR_CHART_WIDTH} 306`} width="100%" role="img" aria-label="Local support index by month with statement counts" style={{ display: 'block', maxWidth: '100%' }}>
        <rect x={X0} y={y(100)} width={X1 - X0} height={y(65) - y(100)} fill={p.supportiveBandWash} />
        <rect x={X0} y={y(35)} width={X1 - X0} height={y(0) - y(35)} fill={p.opposedBandWash} />
        <text x={X0 + 8} y={y(100) + 15} fill={muted} style={small}>Supportive band ≥65</text>
        <text x={X0 + 8} y={y(0) - 7} fill={muted} style={small}>Opposed band ≤35</text>
        {months.map((m, i) => <line key={m.month} x1={x(i)} y1={TOP} x2={x(i)} y2={TOP + PLOT_H} stroke={rule} strokeWidth="1" strokeDasharray="2 4" />)}
        <line x1={X0} y1={y(50)} x2={X1} y2={y(50)} stroke={muted} strokeWidth="1" />
        <text x={X0 + 8} y={y(50) - 6} fill={muted} style={small}>balanced</text>
        {[0, 35, 50, 65, 100].map(v => <text key={v} x={X0 - 10} y={y(v) + 4} textAnchor="end" fill={muted} style={tickText}>{v}</text>)}
        <line x1={X1 - 16} y1={y(index)} x2={X1} y2={y(index)} stroke={markInk} strokeWidth="2" strokeLinecap="round" />
        <text x={X1} y={y(index) - 7} textAnchor="end" fill={ink} style={{ ...small, fontWeight: 600 }}>{index} · {indexLabel}</text>
        {runs.map(([a, b]) => (
          <polyline
            key={`${a}-${b}`}
            data-trend-run={`${months[a]!.month}..${months[b]!.month}`}
            points={months.slice(a, b + 1).map((m, k) => `${x(a + k).toFixed(2)},${y(m.index!).toFixed(2)}`).join(' ')}
            fill="none"
            stroke={markInk}
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        {months.map((m, i) => (m.index === undefined ? null : (
          <g key={m.month} data-trend-point={m.month} data-low-volume={m.count <= PSR_LOW_VOLUME_MONTH_MAX ? 'true' : 'false'} data-joined={joined.has(i) ? 'true' : 'false'}>
            <title>{`${m.label} ${m.month.slice(0, 4)}: index ${m.index} on ${m.count} ${m.count === 1 ? 'statement' : 'statements'}`}</title>
            <circle cx={x(i)} cy={y(m.index)} r="7" fill={p.surface} />
            {m.count <= PSR_LOW_VOLUME_MONTH_MAX
              ? <circle cx={x(i)} cy={y(m.index)} r="4" fill={p.surface} stroke={markInk} strokeWidth="2" />
              : <circle cx={x(i)} cy={y(m.index)} r="4.5" fill={markInk} />}
          </g>
        )))}
        <text x={X0} y="200" fill={muted} style={small}>Dated local statements per month</text>
        {months.map((m, i) => (m.count > 0 ? (
          <g key={m.month} data-trend-count={m.month}>
            <path d={columnPath(x(i) - 8, BASE, 16, barH(m.count))} fill={p.amount[2]} />
            <text x={x(i)} y={BASE - barH(m.count) - 5} textAnchor="middle" fill={ink} style={{ ...small, fontWeight: 600 }}>{m.count}</text>
          </g>
        ) : (
          <text key={m.month} x={x(i)} y={BASE - 5} textAnchor="middle" fill={faint} style={small}>0</text>
        )))}
        <line x1={X0} y1={BASE + 0.5} x2={X1} y2={BASE + 0.5} stroke={rule} strokeWidth="1" />
        {months.map((m, i) => (
          <g key={m.month}>
            <text x={x(i)} y="286" textAnchor="middle" fill={muted} style={{ ...small, fontWeight: 400 }}>{m.label}</text>
            {m.year && <text x={x(i)} y="301" textAnchor="middle" fill={faint} style={{ ...small, fontWeight: 600 }}>{m.year}</text>}
          </g>
        ))}
      </svg>
      {low && <PsrPlainNote theme={theme} label="Low volume" testId="psr-trend-low-volume">{low}</PsrPlainNote>}
    </PsrBlock>
  );
}

/** A column rising from the baseline with a 4px rounded data-end (top). */
function columnPath(x0: number, base: number, w: number, h: number): string {
  if (h <= 0) return '';
  const r = Math.min(4, w / 2, h);
  const f = (n: number) => n.toFixed(2);
  return `M${f(x0)},${f(base)} L${f(x0)},${f(base - h + r)} A${r},${r} 0 0 1 ${f(x0 + r)},${f(base - h)} L${f(x0 + w - r)},${f(base - h)} A${r},${r} 0 0 1 ${f(x0 + w)},${f(base - h + r)} L${f(x0 + w)},${f(base)} Z`;
}

